import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import { useMaterialUIController } from "context";
import { tieneAccesoEntrada } from "modules/menu/menuVisible";
import BuscadorGlobal from "modules/menu/BuscadorGlobal";
import CambiarClaveDialog from "modules/menu/CambiarClaveDialog";
import CambiarSucursalDialog from "modules/menu/CambiarSucursalDialog";
import LogoNegativo from "assets/images/quotas-logo-negativo.png";
import { tieneAccesoRuta, tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import Menu from "@mui/material/Menu";
import MuiMenuItem from "@mui/material/MenuItem";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import { useEmpresaFiscal } from "lib/empresaFiscal";

// Identidad Quotas (opción B de la maqueta): fondo azul grisáceo original,
// azul eléctrico para el ítem activo y cian como acento.
const SIDENAV_WIDTH = 264;
const SIDENAV_BG = "#1A2B4C";
const SIDENAV_BG_DEEP = "#142340";
const SIDENAV_TEXT = "#C6D0EF";
const SIDENAV_TEXT_MUTED = "#7D8CC4";
const SIDENAV_ACTIVE_BG = "#0040FF";
const SIDENAV_ACTIVE_FG = "#FFFFFF";
const SIDENAV_ACTIVE_ACCENT = "#5CE1FF";
const SIDENAV_HOVER_BG = "rgba(255,255,255,0.08)";

function MenuItem({ route, pathname, level, openGroups, onToggle }) {
  const isParent = Array.isArray(route.collapse) && route.collapse.length > 0;
  const isOpen = openGroups[route.key] || false;
  const isActive = pathname === route.route || pathname.startsWith(`${route.route}/`);
  const paddingLeft = 1.6 + level * 1.4;

  if (!isParent) {
    return (
      <ListItemButton
        component={NavLink}
        to={route.route}
        sx={{
          position: "relative",
          pl: paddingLeft,
          pr: 1.4,
          py: 0.85,
          borderRadius: 1.4,
          mb: 0.4,
          color: isActive ? SIDENAV_ACTIVE_FG : SIDENAV_TEXT,
          bgcolor: isActive ? SIDENAV_ACTIVE_BG : "transparent",
          boxShadow: isActive ? "0 6px 18px rgba(0,64,255,0.35)" : "none",
          transition: "all 0.18s ease",
          "&::before": isActive
            ? {
                content: '""',
                position: "absolute",
                left: 6,
                top: 7,
                bottom: 7,
                width: 3,
                borderRadius: 2,
                bgcolor: SIDENAV_ACTIVE_ACCENT,
              }
            : undefined,
          "& .MuiListItemText-primary": {
            fontSize: "0.92rem",
            fontWeight: isActive ? 700 : 500,
            letterSpacing: "0.005em",
          },
          "&:hover": {
            bgcolor: isActive ? SIDENAV_ACTIVE_BG : SIDENAV_HOVER_BG,
            color: "#FFFFFF",
          },
        }}
      >
        {level > 0 && (
          <Box
            sx={{
              width: 3,
              height: 3,
              borderRadius: "50%",
              flexShrink: 0,
              bgcolor: isActive ? SIDENAV_ACTIVE_ACCENT : SIDENAV_TEXT_MUTED,
              mr: 1.1,
            }}
          />
        )}
        {route.adminZone && (
          <SettingsOutlinedIcon
            sx={{ fontSize: 17, color: isActive ? "#FCD34D" : "#F59E0B", mr: 1.1 }}
          />
        )}
        <ListItemText primary={route.name} />
      </ListItemButton>
    );
  }

  return (
    <>
      <ListItemButton
        onClick={() => onToggle(route.key)}
        sx={{
          pl: paddingLeft,
          pr: 1.2,
          py: 0.95,
          borderRadius: 1.4,
          mb: 0.4,
          color: isActive || isOpen ? "#FFFFFF" : SIDENAV_TEXT,
          bgcolor: isActive || isOpen ? SIDENAV_HOVER_BG : "transparent",
          transition: "all 0.18s ease",
          "& .MuiListItemText-primary": {
            fontSize: "0.95rem",
            fontWeight: 600,
            textTransform: "none",
          },
          "&:hover": {
            bgcolor: SIDENAV_HOVER_BG,
            color: "#FFFFFF",
          },
        }}
      >
        <ListItemText primary={route.name} />
        {isOpen ? (
          <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: SIDENAV_TEXT_MUTED }} />
        ) : (
          <ChevronRightRoundedIcon sx={{ fontSize: 18, color: SIDENAV_TEXT_MUTED }} />
        )}
      </ListItemButton>

      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List
          disablePadding
          sx={{
            ml: 1.2,
            pl: 0.4,
            borderLeft: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          {route.collapse
            .filter(
              (child) =>
                child?.type === "subheader" ||
                (child?.type === "collapse" && child?.route && child.route !== "/login")
            )
            .map((child) =>
              child.type === "subheader" ? (
                <Typography
                  key={child.key}
                  sx={{
                    pl: paddingLeft + 0.2,
                    pt: 1,
                    pb: 0.4,
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: SIDENAV_TEXT_MUTED,
                  }}
                >
                  {child.name}
                </Typography>
              ) : (
                <MenuItem
                  key={child.key}
                  route={child}
                  pathname={pathname}
                  level={level + 1}
                  openGroups={openGroups}
                  onToggle={onToggle}
                />
              )
            )}
        </List>
      </Collapse>
    </>
  );
}

// Selector de la empresa fiscal (módulo Fiscal con multi-empresa): cada
// empresa emite con su RNC, certificado y secuencias.
function SelectorEmpresaFiscal() {
  const { activo, empresas, empresa, datos, setEmpresa } = useEmpresaFiscal();
  const [anchor, setAnchor] = useState(null);
  if (!activo) return null;
  return (
    <>
      <Tooltip title="Cambiar de empresa fiscal" placement="right">
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            mt: 1,
            width: "100%",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: 1.1,
            p: 1.1,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.09)",
            transition: "background-color 0.15s, border-color 0.15s",
            "&:hover": { bgcolor: "rgba(255,255,255,0.09)", borderColor: "rgba(255,255,255,0.18)" },
          }}
        >
          <ApartmentRoundedIcon sx={{ fontSize: 19, color: "#9FB1E8", flexShrink: 0, mx: 0.9 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "#9FB1E8", lineHeight: 1.2 }}>
              Empresa fiscal
            </Typography>
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "#FFFFFF",
                lineHeight: 1.25,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {datos?.nombre || `Empresa ${empresa}`}
            </Typography>
            {datos?.rnc ? (
              <Typography sx={{ fontSize: "0.66rem", color: "#9FB1E8", lineHeight: 1.3 }}>
                RNC {datos.rnc}
              </Typography>
            ) : null}
          </Box>
          <UnfoldMoreRoundedIcon sx={{ fontSize: 18, color: "#9FB1E8", flexShrink: 0 }} />
        </ButtonBase>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {empresas.map((e) => {
          const actual = Number(e.empresa) === empresa;
          return (
            <MuiMenuItem
              key={e.empresa}
              selected={actual}
              onClick={() => {
                setAnchor(null);
                if (!actual) setEmpresa(e.empresa);
              }}
              sx={{ gap: 1.2, minWidth: 240 }}
            >
              <Box sx={{ width: 18, display: "flex" }}>
                {actual ? <CheckRoundedIcon sx={{ fontSize: 18 }} /> : null}
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  {e.nombre || `Empresa ${e.empresa}`}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                  {e.rnc ? `RNC ${e.rnc}` : `Empresa ${e.empresa}`}
                </Typography>
              </Box>
            </MuiMenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default function SimpleSidenav({ routes, selectorEmpresaFiscal = false }) {
  const [controller] = useMaterialUIController();
  const { pathname } = useLocation();
  const { usuarioLog } = controller;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cambiarClaveOpen, setCambiarClaveOpen] = useState(false);
  const [cambiarSucursalOpen, setCambiarSucursalOpen] = useState(false);
  // Solo el AdmG cambia su sucursal de trabajo desde la tarjeta del negocio.
  const puedeCambiarSucursal = String(controller?.roleWeb || "").trim() === "AdmG";

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [pathname, isMobile]);

  const visibleRoutes = useMemo(() => {
    const isHeader = (route) => route?.type === "title" || route?.type === "subheader";

    // Las dos ramas traen convenciones distintas en los metadatos de las rutas:
    // `keys` (compartido con BuscadorGlobal) y `aliases` + claves heredadas del
    // padre (umbrellas tipo Compras/Contabilidad). Una entrada es visible si
    // cualquiera de las dos la autoriza.
    const puedeVer = (route, parentKeys) =>
      tieneAccesoEntrada(route, controller.roleDetalles) ||
      tieneAccesoRuta(controller.roleDetalles, route, parentKeys);

    const normalizeRoutes = (currentLevelRoutes = [], parentKeys = []) => {
      const mappedRoutes = currentLevelRoutes
        .map((route) => {
          // Los encabezados (secciones y sub-etiquetas tipo "Reportes") pasan
          // tal cual; abajo se eliminan los que quedan sin items visibles.
          if (isHeader(route)) {
            return { ...route };
          }

          if (!(route?.type === "collapse" && route?.route && route.route !== "/login")) {
            return null;
          }

          // Los hijos solo heredan key/programa umbrella del padre (no aliases de hermanos).
          const keysHijos = [...parentKeys, route?.key, route?.programa].filter(
            (k) => k !== undefined && k !== null && String(k).trim() !== ""
          );

          let visibleChildren = Array.isArray(route.collapse)
            ? normalizeRoutes(route.collapse, keysHijos)
            : [];

          // Umbrella: si el rol tiene la clave del collapse pero ningun hijo
          // quedo visible, se muestran todos los hijos del grupo.
          const tieneUmbrella = [route?.key, route?.programa]
            .filter((k) => k !== undefined && k !== null && String(k).trim() !== "")
            .some((k) => tienePermisosModulo(controller.roleDetalles, k));
          if (
            tieneUmbrella &&
            Array.isArray(route.collapse) &&
            route.collapse.length > 0 &&
            visibleChildren.length === 0
          ) {
            visibleChildren = route.collapse.filter(
              (child) => child?.type === "collapse" && child?.route && child.route !== "/login"
            );
          }

          if (visibleChildren.some((child) => !isHeader(child))) {
            return { ...route, collapse: visibleChildren };
          }

          return puedeVer(route, parentKeys) ? { ...route, collapse: [] } : null;
        })
        .filter(Boolean);

      // Un encabezado se muestra solo si lo sigue un item visible antes del
      // proximo encabezado (o de la zona admin, que tiene su propio rotulo).
      return mappedRoutes.filter((route, idx) => {
        if (!isHeader(route)) return true;
        const next = mappedRoutes[idx + 1];
        return Boolean(next) && !isHeader(next) && !next.adminZone;
      });
    };

    return normalizeRoutes(routes);
  }, [routes, controller.roleDetalles]);

  const usuarioActual = usuarioLog || "Usuario";
  // Nombre del negocio (multi-tenant) y sucursal del usuario para la
  // tarjeta del encabezado.
  const nombreNegocio = useMemo(() => {
    const valor = controller?.nombreNegocio;
    if (valor && valor !== "false") return valor;
    return localStorage.getItem("nombrenegocio") || "";
  }, [controller?.nombreNegocio]);
  const sucursalNombre = useMemo(() => {
    const nombre = (localStorage.getItem("sucursalnombre") || "").trim();
    if (nombre) return nombre;
    const codigo = (localStorage.getItem("sucursal") || "").trim();
    return codigo && codigo !== "undefined" && codigo !== "null" ? `Sucursal ${codigo}` : "";
  }, []);
  const usuarioInicial = usuarioActual ? usuarioActual.toString().trim().charAt(0).toUpperCase() : "U";
  const usuarioEmail = controller?.emailLog || `${usuarioActual.toLowerCase().replace(/\s+/g, ".")}@suitorbit.com`;

  const [openGroups, setOpenGroups] = useState(() => {
    const getOpenGroups = (items = [], current = {}) => {
      items.forEach((route) => {
        if (Array.isArray(route.collapse) && route.collapse.length > 0) {
          const hasActiveChild = route.collapse.some(
            (child) => pathname === child.route || pathname.startsWith(`${child.route}/`)
          );

          if (hasActiveChild || pathname.startsWith(route.route)) {
            current[route.key] = true;
          }

          getOpenGroups(route.collapse, current);
        }
      });
      return current;
    };

    return getOpenGroups(visibleRoutes);
  });

  useEffect(() => {
    const getOpenGroups = (items = [], current = {}) => {
      items.forEach((route) => {
        if (Array.isArray(route.collapse) && route.collapse.length > 0) {
          const hasActiveChild = route.collapse.some(
            (child) => pathname === child.route || pathname.startsWith(`${child.route}/`)
          );

          if (hasActiveChild || pathname.startsWith(route.route)) {
            current[route.key] = true;
          }

          getOpenGroups(route.collapse, current);
        }
      });
      return current;
    };

    setOpenGroups((prev) => ({ ...prev, ...getOpenGroups(visibleRoutes) }));
  }, [pathname, visibleRoutes]);

  const handleToggle = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSignOut = () => {
    localStorage.clear();
  };

  const sidenavContent = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        background: `linear-gradient(180deg, ${SIDENAV_BG} 0%, ${SIDENAV_BG_DEEP} 100%)`,
        color: SIDENAV_TEXT,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ position: "relative", px: 2.2, pt: 2.2, pb: 1 }}>
        <Box sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
          <Box
            component="img"
            src={LogoNegativo}
            alt="Quotas"
            sx={{ height: 52, width: "auto" }}
          />
        {isMobile && (
          <IconButton
            onClick={() => setMobileOpen(false)}
            sx={{
              position: "absolute",
              right: -8,
              top: "50%",
              transform: "translateY(-50%)",
              color: SIDENAV_TEXT_MUTED,
              "&:hover": { color: "#FFFFFF", bgcolor: "rgba(255,255,255,0.06)" },
            }}
            aria-label="Cerrar menú"
          >
            <CloseRoundedIcon />
          </IconButton>
        )}
        </Box>
        {nombreNegocio || sucursalNombre ? (
          <Tooltip title={puedeCambiarSucursal ? "Cambiar de sucursal" : ""} placement="right">
            <Box
              component={puedeCambiarSucursal ? ButtonBase : "div"}
              onClick={puedeCambiarSucursal ? () => setCambiarSucursalOpen(true) : undefined}
              sx={{
                mt: 1.4,
                width: "100%",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 1.1,
                p: 1.1,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.09)",
                ...(puedeCambiarSucursal && {
                  cursor: "pointer",
                  transition: "background-color 0.15s, border-color 0.15s",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.09)",
                    borderColor: "rgba(255,255,255,0.18)",
                  },
                }),
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.4,
                  bgcolor: SIDENAV_ACTIVE_BG,
                  boxShadow: "0 4px 12px rgba(0,64,255,0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <StorefrontRoundedIcon sx={{ fontSize: 19, color: "#FFFFFF" }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: "#FFFFFF",
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {nombreNegocio || "Quotas"}
                </Typography>
                {sucursalNombre ? (
                  <Typography
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      color: "#9FB1E8",
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: "2px",
                        bgcolor: SIDENAV_ACTIVE_ACCENT,
                        flexShrink: 0,
                        display: "inline-block",
                      }}
                    />
                    {sucursalNombre}
                  </Typography>
                ) : null}
              </Box>
              {puedeCambiarSucursal ? (
                <UnfoldMoreRoundedIcon sx={{ fontSize: 18, color: "#9FB1E8", flexShrink: 0 }} />
              ) : null}
            </Box>
          </Tooltip>
        ) : null}
        {selectorEmpresaFiscal ? <SelectorEmpresaFiscal /> : null}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 2 }} />

      <BuscadorGlobal routes={routes} />

      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255,255,255,0.18)",
              borderRadius: 10,
            },
          }}
        >
          <List sx={{ p: 1.2 }}>
            {visibleRoutes.map((route, idx) => {
              if (route.type === "title") {
                return (
                  <Typography
                    key={route.key}
                    sx={{
                      px: 1.6,
                      pt: idx === 0 ? 0.4 : 1.8,
                      pb: 0.6,
                      fontSize: "0.62rem",
                      fontWeight: 800,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: SIDENAV_TEXT_MUTED,
                    }}
                  >
                    {route.title}
                  </Typography>
                );
              }

              return (
              <Box key={route.key}>
                {route.adminZone && !visibleRoutes[idx - 1]?.adminZone && (
                  <>
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.10)", mx: 0.4, mt: 1.2, mb: 1 }} />
                    <Typography
                      sx={{
                        px: 1.6,
                        pb: 0.6,
                        fontSize: "0.62rem",
                        fontWeight: 800,
                        letterSpacing: "0.14em",
                        color: SIDENAV_TEXT_MUTED,
                      }}
                    >
                      ADMINISTRACIÓN
                    </Typography>
                  </>
                )}
                <MenuItem
                  route={route}
                  pathname={pathname}
                  level={0}
                  openGroups={openGroups}
                  onToggle={handleToggle}
                />
              </Box>
              );
            })}
          </List>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 1.2, mt: 0.5 }} />

        {/* Tarjeta del usuario: abre "Cambiar mi clave" (cualquier usuario,
            tenga o no acceso a Configuración). */}
        <Tooltip title="Cambiar mi clave" placement="top">
          <ButtonBase
            onClick={() => setCambiarClaveOpen(true)}
            sx={{
              mx: 1.2,
              mt: 1,
              mb: 0.6,
              p: 1.2,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              textAlign: "left",
              gap: 1.2,
              borderRadius: 1.4,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
              "&:hover .icono-clave": { color: "#FFFFFF" },
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: SIDENAV_ACTIVE_BG,
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: "0.92rem",
              }}
            >
              {usuarioInicial}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontSize: "0.86rem",
                  fontWeight: 700,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {usuarioActual}
              </Typography>
              <Typography
                sx={{
                  color: SIDENAV_TEXT_MUTED,
                  fontSize: "0.72rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {usuarioEmail}
              </Typography>
            </Box>
            <KeyOutlinedIcon
              className="icono-clave"
              sx={{ fontSize: 18, color: SIDENAV_TEXT_MUTED }}
            />
          </ButtonBase>
        </Tooltip>
        <CambiarClaveDialog open={cambiarClaveOpen} onClose={() => setCambiarClaveOpen(false)} />
        {puedeCambiarSucursal ? (
          <CambiarSucursalDialog
            open={cambiarSucursalOpen}
            onClose={() => setCambiarSucursalOpen(false)}
          />
        ) : null}

        <Box sx={{ p: 1.2, pt: 0.5 }}>
          <ListItemButton
            component={NavLink}
            to="/login"
            onClick={handleSignOut}
            sx={{
              py: 0.9,
              px: 1.4,
              borderRadius: 1.4,
              color: "#FFB7C8",
              bgcolor: "rgba(225,29,72,0.08)",
              border: "1px solid rgba(225,29,72,0.2)",
              "& .MuiListItemText-primary": { fontSize: "0.9rem", fontWeight: 600 },
              "&:hover": {
                bgcolor: "rgba(225,29,72,0.16)",
                color: "#FFFFFF",
              },
            }}
          >
            <LogoutRoundedIcon sx={{ fontSize: 18, mr: 1.2 }} />
            <ListItemText primary="Sign out" />
          </ListItemButton>
        </Box>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <IconButton
          onClick={() => setMobileOpen(true)}
          aria-label="Abrir menú"
          sx={{
            position: "fixed",
            top: 10,
            left: 10,
            zIndex: (t) => t.zIndex.drawer + 2,
            width: 42,
            height: 42,
            borderRadius: 1.4,
            bgcolor: SIDENAV_BG,
            color: "#FFFFFF",
            boxShadow: "0 6px 18px rgba(15,23,42,0.25)",
            display: mobileOpen ? "none" : "inline-flex",
            "&:hover": { bgcolor: SIDENAV_BG_DEEP },
          }}
        >
          <MenuRoundedIcon />
        </IconButton>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: { xs: "84vw", sm: SIDENAV_WIDTH },
              maxWidth: SIDENAV_WIDTH + 20,
              border: "none",
              backgroundColor: "transparent",
            },
          }}
        >
          {sidenavContent}
        </Drawer>
      </>
    );
  }

  return (
    <Box
      sx={{
        width: SIDENAV_WIDTH,
        flexShrink: 0,
        position: "sticky",
        top: 0,
        height: "100vh",
      }}
    >
      {sidenavContent}
    </Box>
  );
}
